/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/** Shared VIN validation rules and user-facing copy. */
export const VIN_MAX_LENGTH = 17;

// GM VIN: the last 8 characters up to the full 17; excludes I, O, Q.
export const VIN_RE = /^[A-HJ-NPR-Z0-9]{8,17}$/;

export const normalizeVin = (value: string): string => value.trim().toUpperCase();

export const isValidVin = (value: string): boolean => VIN_RE.test(normalizeVin(value));

export const isFullVin = (value: string): boolean =>
  normalizeVin(value).length === VIN_MAX_LENGTH;

/** Prefer AVP identity when valid; otherwise what the user typed. */
export const canonicalVin = (typed: string, fromDetails?: string | null): string => {
  const fromAvp = fromDetails ? normalizeVin(fromDetails) : '';
  if (fromAvp && isValidVin(fromAvp)) return fromAvp;
  return typed;
};

// A full 17-character VIN embedded in decoded barcode text (Code 39 / Data Matrix / QR).
const VIN17_RE = /[A-HJ-NPR-Z0-9]{17}/;

/** Extract a normalized 17-char VIN from raw barcode text, or null when none found. */
export const extractVin = (raw: string): string | null => {
  const compact = raw.toUpperCase().replace(/\s+/g, '');
  const match = compact.match(VIN17_RE);
  return match && isValidVin(match[0]) ? match[0] : null;
};

/** i18n keys for VIN field copy; render with t(key). */
export const VIN_PLACEHOLDER = 'vin.placeholder';

export const VIN_HINT = 'vin.hint';
/** Diagnostic-program explanation shared by Home and the picker. */
export const DIAG_PROGRAM_HINT = 'vin.diagProgramHint';
